import { Button, LoadSpinner } from '@calizahq/react-components-v2'
import { getMutationsVariables, transformCoreAmenityToRusty } from '@sales-app/context/workspace/homeFilters.util'
import { getLengthOfStayBucket, lengthOfStayBucketLabel } from '@sales-app/context/workspace/lengthOfStayBucket.util'
import { WorkspaceAction } from '@sales-app/context/workspace/workspace.actions'
import { useWorkspaceContext } from '@sales-app/context/workspace/workspace.hooks'
import { HomeFilters, WorkspaceTab, WorkspaceView } from '@sales-app/context/workspace/workspace.type'
import { getWorkspacePath } from '@sales-app/context/workspace/workspace.util'
import { PotentialBookingsMutations } from '@sales-app/graphql/mutations/potentialBookings.mutations'
import React, { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAlertContext, useUserContext } from '@/context'
import { AgentRoles } from '@/context/user/user.types'
import { useGraphqlMutation } from '@/hooks/useGraphql'

import { SectionContainer } from '../PotentialBooking'
import { ControlRow } from './ControlRow'
import { AgentSelection } from './Filters/AgentSelection'
import { AmenitySelection } from './Filters/AmenitySelection'
import { BathSelection } from './Filters/BathSelection'
import { BedSelection } from './Filters/BedSelection'
import { CompetitorSelection } from './Filters/CompetitorSelection'
import { MarketSelection } from './Filters/MarketSelection'
import { MoveInSelection } from './Filters/MoveInSelection'
import { MoveOutSelection } from './Filters/MoveOutSelection'
import { NotesSelection } from './Filters/NotesSelection'
import { PriceRangeSelection } from './Filters/PriceRangeSelection'
import { ReservationTypeSelection } from './Filters/ReservationTypeSelection'
import { SourceSelection } from './Filters/SourceSelection'

type Props = {
  activeWorkspaceTab: WorkspaceTab
  currentFilters: HomeFilters
  disabled?: boolean
  isLoading: boolean
  refetchDetails: () => void
}

export const ControlSection: FC<Props> = ({
  activeWorkspaceTab,
  refetchDetails: refetch,
  isLoading,
  currentFilters,
  disabled = false,
}) => {
  const activeFilters = activeWorkspaceTab.potentialBookingFilters || { listView: true }
  const { displayCity, source, agent, reservationType } = activeFilters
  const keyString = JSON.stringify({ displayCity, source, agent, reservationType }) || ''
  const controlSectionKey = btoa(keyString)
  const { setAlert } = useAlertContext()
  const navigate = useNavigate()
  const [, workspaceDispatch] = useWorkspaceContext()
  const { user } = useUserContext()
  const isSupervisor = user?.agent?.role === AgentRoles.SUPERVISOR

  const { mutate: createPotentialBookingMutation, isPending: isCreateLoading } = useGraphqlMutation(
    PotentialBookingsMutations.createPotentialBooking(),
    {
      onSuccess: (res: { createPotentialBooking: { id: number } }) => {
        const id = res?.createPotentialBooking?.id || null
        refetch()
        if (id) {
          setAlert(`New potential booking created for ${activeWorkspaceTab.label}`, 'success')
          workspaceDispatch(
            WorkspaceAction.updateTab({
              ...activeWorkspaceTab,
              potentialBookingId: id,
            }),
          )
          const path = getWorkspacePath({
            view: WorkspaceView.POTENTIALBOOKING,
            type: activeWorkspaceTab.bookablePersonType,
            id: activeWorkspaceTab.bookablePersonId,
            potentialBookingId: id,
          })

          navigate(path)
        }
      },
    },
  )

  const { mutate: updatePotentialBookingMutation } = useGraphqlMutation(
    PotentialBookingsMutations.updatePotentialBooking(),
    {
      onSuccess: () => {
        setAlert(`${activeWorkspaceTab.label} potential booking updated`, 'success')
        refetch()
      },
    },
  )

  const createPotentialBooking = () => {
    if ((!!displayCity?.id || isSupervisor) && !!source?.id) {
      createPotentialBookingMutation({
        potentialBooking: {
          bookablePerson: {
            bookablePersonId: activeWorkspaceTab.bookablePersonId,
            bookablePersonType: activeWorkspaceTab.bookablePersonType,
          },
          ...transformCoreAmenityToRusty(activeWorkspaceTab.potentialBookingFilters?.amenity || []),
          ...getMutationsVariables(activeWorkspaceTab.potentialBookingFilters),
        },
      })
    } else {
      setAlert(` Market and/or Source fields are needed to create potential booking.`, 'info')
    }
  }

  // currentFilters comes from the fetched potential booking, so this is true only when the agent picked a status
  // since the last load; the tab snapshot can be stale and must not decide it.
  const statusChanged = activeWorkspaceTab.potentialBookingFilters?.status?.id !== currentFilters.status?.id

  const updatePotentialBooking = () => {
    if (activeWorkspaceTab.potentialBookingId) {
      if (!!source?.id) {
        updatePotentialBookingMutation({
          potentialBooking: {
            potentialBookingId: activeWorkspaceTab.potentialBookingId,
            bookablePerson: {
              bookablePersonId: activeWorkspaceTab.bookablePersonId,
              bookablePersonType: activeWorkspaceTab.bookablePersonType,
            },
            potentialBookingStatusId: statusChanged
              ? activeWorkspaceTab.potentialBookingFilters?.status?.id
              : undefined,
            ...transformCoreAmenityToRusty(activeWorkspaceTab.potentialBookingFilters?.amenity || []),
            ...getMutationsVariables(activeWorkspaceTab.potentialBookingFilters),
            agentId: agent === null ? null : agent?.id,
          },
        })
      } else {
        setAlert(`Source is required fields to update Potential booking.`, 'info')
      }
    }
  }

  return (
    <SectionContainer
      className="flex h-fit-content min-h-screen w-full flex-col overflow-visible overflow-y-scroll md:mr-6 md:w-104"
      id={`${controlSectionKey}`}
      key={`${controlSectionKey}`}
    >
      {isLoading ? (
        <div className="flex h-48 w-full items-center justify-center ">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          {!disabled && (
            <>
              <div className="mb-4 w-full">
                {!activeWorkspaceTab.potentialBookingId ? (
                  <Button
                    className="w-full bg-text-semantic-success"
                    disabled={isCreateLoading}
                    onClick={createPotentialBooking}
                  >
                    {' '}
                    Create
                  </Button>
                ) : (
                  <Button className="w-full bg-text-semantic-success" onClick={updatePotentialBooking}>
                    {' '}
                    Update
                  </Button>
                )}
              </div>
              <hr className="border-secondary" />
            </>
          )}
          <ControlRow
            label="Market"
            renderRightContent={() => <MarketSelection currentFilters={activeFilters} />}
          ></ControlRow>
          <ControlRow
            label="Reservation Type"
            renderRightContent={() => <ReservationTypeSelection reservationType={activeFilters.reservationType} />}
          ></ControlRow>
          <ControlRow
            label="Length of Stay"
            rightText={lengthOfStayBucketLabel(
              getLengthOfStayBucket(activeFilters.moveInStartDate, activeFilters.moveOutStartDate),
            )}
          ></ControlRow>
          <ControlRow
            label="Move In"
            renderRightContent={() => <MoveInSelection currentFilters={activeFilters} />}
          ></ControlRow>
          <ControlRow
            label="Move Out"
            renderRightContent={() => <MoveOutSelection currentFilters={activeFilters} />}
          ></ControlRow>
          <ControlRow
            label="Price Range"
            renderRightContent={() => <PriceRangeSelection currentFilters={activeFilters} />}
          />
          <ControlRow label="Bed #" renderRightContent={() => <BedSelection beds={activeFilters.beds} />} />
          <ControlRow label="Bath #" renderRightContent={() => <BathSelection baths={activeFilters.baths} />} />
          <AmenitySelection currentFilters={activeFilters} />
          <hr className="border-secondary" />
          <h4 className="flex w-full py-4 text-tertiary">Internal Details</h4>
          {activeWorkspaceTab.potentialBookingId && (
            <ControlRow
              label="Agent"
              renderRightContent={() => <AgentSelection currentFilters={activeFilters} />}
            ></ControlRow>
          )}
          <ControlRow
            label="Source"
            renderRightContent={() => <SourceSelection currentFilters={activeFilters} />}
          ></ControlRow>
          <ControlRow
            className="flex-0"
            label="Competitors"
            renderRightContent={() => <CompetitorSelection currentFilters={activeFilters} />}
          ></ControlRow>
          {currentFilters.utmSource && (
            <ControlRow label="UTM Source" rightText={currentFilters.utmSource || ''}></ControlRow>
          )}
          {activeWorkspaceTab.potentialBookingId && (
            <ControlRow label="Created Date" rightText={currentFilters.createdAt || ''}></ControlRow>
          )}
          <NotesSelection currentFilters={activeFilters}></NotesSelection>
        </>
      )}
    </SectionContainer>
  )
}
